/**
 * The receipt template, on the wire.
 *
 * The console writes one of these, the server stores it as a document, and the
 * kiosk prints from it — so this schema is the only thing standing between an
 * operator's arrangement and a customer's piece of paper. It is strict on
 * purpose: an unknown key here is a console from a different deploy, and
 * silently dropping it would lose an edit somebody made.
 */
import { z } from 'zod'
import { REQUIRED_SECTIONS, SECTION_KINDS } from '@mgc/receipt/template'
import { zId } from './scalars'

/**
 * A logo, as a `data:` URL, capped.
 *
 * 256 KB of base64 is about 190 KB of PNG, which is enormous for a one-bit
 * image 576 dots wide and still comfortably inside the server's 1 MB
 * `bodyLimit` — the point of the cap is that without one an operator uploading
 * a photograph gets a 413 they read as "save is broken".
 */
const zLogoData = z
  .string()
  .max(256 * 1024, 'that logo is too large; the console dithers to one bit before uploading')
  .refine((data) => data.startsWith('data:image/png;base64,'), {
    message: 'a logo is a base64 PNG data URL, prepared by the console'
  })

export const zTemplateLogo = z.object({
  data: zLogoData,
  /** The printable width it was dithered for. 384 on 58mm, 576 on 80mm. */
  dots: z.number().int().min(8).max(1024),
  height: z.number().int().min(1).max(2048)
})

export const zTemplateEntry = z
  .object({
    id: zId,
    kind: z.enum(SECTION_KINDS),
    enabled: z.boolean(),
    /** The operator's own words: a `text` block, or a label override. */
    text: z.string().max(240).optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
    bold: z.boolean().optional(),
    size: z.enum(['normal', 'large']).optional(),
    lines: z.number().int().min(0).max(8).optional()
  })
  .strict()

export const zReceiptTemplate = z
  .object({
    entries: z.array(zTemplateEntry).min(1).max(64),
    logo: zTemplateLogo.nullable(),
    labels: z
      .object({
        eatIn: z.string().max(64),
        takeOut: z.string().max(64),
        subtotal: z.string().max(64),
        discount: z.string().max(64),
        vat: z.string().max(64),
        total: z.string().max(64),
        change: z.string().max(64),
        paidBy: z.record(z.string(), z.string().max(64)),
        lottery: z.string().max(64),
        billId: z.string().max(64),
        fiscalPending: z.string().max(120),
        company: z.string().max(64),
        wifi: z.string().max(64),
        thanks: z.string().max(120),
        orderNumber: z.string().max(64),
        // The redesign's labels, optional because a template saved before it
        // has none of them — `buildSlip` fills the gaps from `MN`.
        sellerName: z.string().max(120).optional(),
        sellerTin: z.string().max(120).optional(),
        branch: z.string().max(120).optional(),
        branchAddress: z.string().max(120).optional(),
        date: z.string().max(120).optional(),
        product: z.string().max(120).optional(),
        quantity: z.string().max(120).optional(),
        unitPrice: z.string().max(120).optional(),
        amount: z.string().max(120).optional(),
        paid: z.string().max(120).optional(),
        paymentMethod: z.string().max(120).optional(),
        ebarimtAmount: z.string().max(120).optional(),
        wifiPassword: z.string().max(120).optional()
      })
      .strict()
  })
  .strict()
  /*
   * What the law will not let an operator delete.
   *
   * e-Barimt registers a sale three ways and the customer picks: the QR code,
   * the ДДТД, or the lottery number. All three ride on the fiscal block, so a
   * template without it is a shop whose customers cannot claim their VAT — and
   * nobody finds out until a tax season. The totals go with it for a plainer
   * reason: a receipt that does not say what was charged is not a receipt.
   *
   * Checked here rather than only in the console, because a rule that only the
   * screen knows is a rule a hand-made POST does not have to keep.
   */
  .superRefine((template, ctx) => {
    for (const kind of REQUIRED_SECTIONS) {
      const entry = template.entries.find((e) => e.kind === kind)
      if (entry === undefined || !entry.enabled) {
        ctx.addIssue({
          code: 'custom',
          path: ['entries'],
          message: `«${kind}» cannot be removed from a receipt: e-Barimt and the customer both need it`
        })
      }
    }
    const ids = template.entries.map((e) => e.id)
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({ code: 'custom', path: ['entries'], message: 'two entries share an id' })
    }
  })

export type ReceiptTemplateWire = z.infer<typeof zReceiptTemplate>

/**
 * What the kiosk reads.
 *
 * `revision` is what makes a re-read cheap: the page holds the last one it
 * cached and the server answers `unchanged` rather than resending a document
 * with a logo in it, which is the same bargain `catalogue.revision` strikes.
 */
export const zResolvedTemplate = z.object({
  revision: z.string(),
  /** Whether this shop has its own, or is following the chain's. */
  source: z.enum(['branch', 'global', 'default']),
  template: zReceiptTemplate
})

export type ResolvedTemplate = z.infer<typeof zResolvedTemplate>
